import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  FormHelperText,
  Button,
  Divider,
  useMediaQuery,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Space } from 'v2/components/Shared/Space';
import { useGetPlanChangeConfig } from 'v2/hooks/useGetPlanChangeConfig';
import productService from 'v2/services/product';

import type { SetPlanChangeConfigPayload, SubscriptionPlan } from 'v2/interfaces/product';

import { ProductsList } from './ProductsList';
import { useStyles } from './styles';

type ChangePlanModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  plan: SubscriptionPlan;
  productId: number;
};

type Plan = {
  plan_id: number;
  variation: boolean;
};

function ChangePlanModal({ isOpen, handleClose, plan, productId }: ChangePlanModalProps) {
  const [sameProduct, setSameProduct] = useState(plan.plan_trocaplanoproduto);
  const [variations, setVariations] = useState(plan.plan_trocaplanovariacao);
  const [selectedPlans, setSelectedPlans] = useState<Plan[]>([]);
  const queryClient = useQueryClient();

  const classes = useStyles();
  const isMobile = useMediaQuery('(max-width: 600px)');
  const { data, isLoading, isError } = useGetPlanChangeConfig(plan.plan_id);
  const plans = data || [];

  const setPlanChangeConfigMutation = useMutation((payload: SetPlanChangeConfigPayload) => productService.setPlanChangeConfig(payload));

  function handleSelectPlan(plan: Plan) {
    const isSelected = selectedPlans.some((selectedPlan) => selectedPlan.plan_id === plan.plan_id);
    if (isSelected) {
      setSelectedPlans(selectedPlans.filter((selectedPlan) => selectedPlan.plan_id !== plan.plan_id));
      return;
    }
    setSelectedPlans([...selectedPlans, plan]);
  }

  function handleSelectAllPlans(isVariation = false) {
    const filteredPlans = plans.filter((plan) => plan.variation === isVariation);
    const isAllSelected = filteredPlans.every((plan) => selectedPlans.some((selectedPlan) => selectedPlan.plan_id === plan.plan_id));
    if (isAllSelected) {
      setSelectedPlans(selectedPlans.filter((selectedPlan) => selectedPlan.variation !== isVariation));
      return;
    }
    const notSelectedYet = filteredPlans.filter((plan) => !selectedPlans.some((selectedPlan) => selectedPlan.plan_id === plan.plan_id));
    setSelectedPlans([...selectedPlans, ...notSelectedYet]);
  }

  const handleSelectAllVariations = () => handleSelectAllPlans(true);

  async function handleSave() {
    try {
      if (sameProduct) {
        const plans = selectedPlans.filter((plan) => !plan.variation);
        if (!plans.length) {
          toast.error('Selecione pelo menos um plano para permitir troca entre planos do mesmo produto');
          return;
        }
      }

      if (variations) {
        const plans = selectedPlans.filter((plan) => plan.variation);
        if (!plans.length) {
          toast.error('Selecione pelo menos um plano para permitir troca entre planos de variações do produto');
          return;
        }
      }

      await setPlanChangeConfigMutation.mutateAsync({
        targetPlanId: plan.plan_id,
        allows_plan_change_for_product: sameProduct,
        allows_plan_change_for_variation: variations,
        plan_changeconfig: selectedPlans.map((plan) => ({ ...plan, selected: true })),
      });

      queryClient.setQueryData(['product', productId, 'plans'], (oldData: SubscriptionPlan[]) =>
        oldData.map((oldPlan) => {
          if (oldPlan.plan_id === plan.plan_id) {
            return {
              ...oldPlan,
              plan_trocaplanoproduto: sameProduct,
              plan_trocaplanovariacao: variations,
              plan_changeconfig: data.map((plan) => ({
                ...plan,
                selected: selectedPlans.some((selectedPlan) => selectedPlan.plan_id === plan.plan_id),
              })),
            };
          }
          return oldPlan;
        })
      );

      toast.success('Troca de plano configurada com sucesso!');
      handleClose();
    } catch (err) {
      toast.error('Falha ao configurar troca de plano');
      console.error(err);
    }
  }

  useEffect(() => {
    if (isLoading || !data?.length) return;
    const selectedPlans = data.filter((plan) => plan.selected);
    setSelectedPlans(selectedPlans);
  }, [isLoading, data]);

  const sameProductPlans = plans.filter((plan) => !plan.variation);
  const sameProductSelectedPlans = selectedPlans.filter((plan) => !plan.variation);
  const variationsPlans = plans.filter((plan) => plan.variation);
  const variationsSelectedPlans = selectedPlans.filter((plan) => plan.variation);
  const hasNoSameProductPlans = !sameProductPlans.length;
  const hasNoVariationsPlans = !variationsPlans.length;

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth='md' fullWidth fullScreen={isMobile}>
      <DialogTitle disableTypography className={classes.title}>
        Troca de plano
      </DialogTitle>
      <DialogContent className={classes.content}>
        <p className={classes.description}>Você pode habilitar ou desabilitar a troca de plano para este produto</p>
        <div>
          <FormControlLabel
            label='Permitir troca de plano entre planos do mesmo produto'
            control={<Switch id='change-plan-modal-same-product-switch' color='primary' />}
            checked={sameProduct}
            onChange={() => setSameProduct(!sameProduct)}
            disabled={isLoading || hasNoSameProductPlans}
          />
          <FormHelperText>Uma vez habilitado o cliente poderá efetuar a troca de plano para os planos deste produto</FormHelperText>
          {hasNoSameProductPlans && (
            <>
              <Space axis='vertical' size={8} />
              <Alert severity='warning'>Você não possui outros planos cadastrados</Alert>
            </>
          )}
        </div>
        {sameProduct && (
          <ProductsList
            data={sameProductPlans}
            isLoading={isLoading}
            isError={isError}
            selectedPlans={sameProductSelectedPlans}
            handleSelectPlan={handleSelectPlan}
            handleSelectAllPlans={handleSelectAllPlans}
          />
        )}
        <Divider />
        <div>
          <FormControlLabel
            label='Permitir troca de plano entre planos de variações do produto'
            control={<Switch id='change-plan-modal-variations-switch' color='primary' />}
            checked={variations}
            onChange={() => setVariations(!variations)}
            disabled={isLoading || hasNoVariationsPlans}
          />
          <FormHelperText>Uma vez habilitado o cliente poderá efetuar a troca deste plano para os planos de outra variação</FormHelperText>
          {hasNoVariationsPlans && (
            <>
              <Space axis='vertical' size={8} />
              <Alert severity='warning'>Você não possui outros planos de variações cadastrados</Alert>
            </>
          )}
        </div>
        {variations && (
          <ProductsList
            data={variationsPlans}
            isLoading={isLoading}
            isError={isError}
            selectedPlans={variationsSelectedPlans}
            handleSelectPlan={handleSelectPlan}
            handleSelectAllPlans={handleSelectAllVariations}
            displayProduct
          />
        )}
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button variant='outlined' onClick={handleClose}>
          Cancelar
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSave}
          disabled={setPlanChangeConfigMutation.isLoading}
          startIcon={setPlanChangeConfigMutation.isLoading && <CircularProgress size={20} color='inherit' />}
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export { ChangePlanModal };
